import type { ColumnFiltersState } from '@tanstack/react-table';
import { endOfDay, isValid, startOfDay } from 'date-fns';
import {
	AuditAction,
	DealEventCategory,
	DealEventStatus,
} from '@dealstack/shared/lib/backendClient';
import { dealEventTypeFor } from '@dealstack/shared/lib/common/business/dealEvent';
import {
	DealEventLogQuery,
	DealEventLogTab,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';

export const DEAL_EVENT_LOG_TAB_CATEGORIES: Record<
	DealEventLogTab,
	DealEventCategory[] | undefined
> = {
	[DealEventLogTab.ALL_EVENTS]: undefined,
	[DealEventLogTab.INVESTOR_ACTIVITY]: [DealEventCategory.PARTICIPANT],
	[DealEventLogTab.CAPITAL_EVENTS]: [DealEventCategory.CAPITAL],
	[DealEventLogTab.TEAM_AND_ACCESS]: [DealEventCategory.ACCESS],
	[DealEventLogTab.MESSAGING]: [DealEventCategory.MESSAGE],
};

export enum DealEventLogColumn {
	DESCRIPTION = 'description',
	TYPE = 'type',
	ACTOR = 'actor',
	ENTITY = 'entity',
	STATUS = 'status',
	OCCURRED_AT = 'occurredAt',
}

const readFilter = (filters: ColumnFiltersState, id: DealEventLogColumn): unknown =>
	filters.find((filter) => filter.id === id)?.value;

const asStrings = (value: unknown): string[] | undefined => {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const values = value.filter((entry): entry is string => typeof entry === 'string' && !!entry);

	return values.length ? values : undefined;
};

const asText = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim() ? value.trim() : undefined;

export const readTextFilter = (
	filters: ColumnFiltersState,
	column: DealEventLogColumn
): string | undefined => asText(readFilter(filters, column));

const asOne = (value: unknown): string[] | undefined => {
	const text = asText(value);

	return text ? [text] : undefined;
};

const asEnums = <T extends string>(allowed: T[], value: unknown): T[] | undefined => {
	const values = asStrings(value)?.filter((entry): entry is T =>
		allowed.some((candidate) => candidate === entry)
	);

	return values?.length ? values : undefined;
};

// The URL hook restores a range as Dates, but a hand-edited param arrives as strings.
const asDate = (value: unknown): Date | undefined => {
	if (value instanceof Date) {
		return isValid(value) ? value : undefined;
	}

	if (typeof value !== 'string') {
		return undefined;
	}

	const parsed = new Date(value);

	return isValid(parsed) ? parsed : undefined;
};

const toDateRange = (value: unknown): { from?: string; to?: string } => {
	if (!Array.isArray(value)) {
		return {};
	}

	const from = asDate(value[0]);
	const to = asDate(value[1]);

	return {
		from: from && startOfDay(from).toISOString(),
		to: to && endOfDay(to).toISOString(),
	};
};

/**
 * What the type column means per tab: concrete types on team and access, a category on all
 * events — sent separately — and on capital the table itself, which stands for every action
 * recorded against it. Messaging has no type filter; email is its only channel.
 */
const toTypes = (tab: DealEventLogTab, value: unknown): string[] | undefined => {
	if (tab === DealEventLogTab.TEAM_AND_ACCESS) {
		return asStrings(value);
	}

	if (tab !== DealEventLogTab.CAPITAL_EVENTS) {
		return undefined;
	}

	return asStrings(value)?.flatMap((table) =>
		Object.values(AuditAction).map((action) => dealEventTypeFor(table, action))
	);
};

export const getDealEventLogQuery = (
	tab: DealEventLogTab,
	filters: ColumnFiltersState = []
): DealEventLogQuery => {
	const type = readFilter(filters, DealEventLogColumn.TYPE);
	const entity = readFilter(filters, DealEventLogColumn.ENTITY);
	const { from, to } = toDateRange(readFilter(filters, DealEventLogColumn.OCCURRED_AT));
	const selectedCategories = asEnums(Object.values(DealEventCategory), type);

	return {
		categories:
			tab === DealEventLogTab.ALL_EVENTS
				? selectedCategories
				: DEAL_EVENT_LOG_TAB_CATEGORIES[tab],
		types: toTypes(tab, type),
		statuses: asEnums(
			Object.values(DealEventStatus),
			readFilter(filters, DealEventLogColumn.STATUS)
		),
		recipientEmails: tab === DealEventLogTab.MESSAGING ? asOne(entity) : undefined,
		securityIds: tab === DealEventLogTab.CAPITAL_EVENTS ? asStrings(entity) : undefined,
		targetUserIds: tab === DealEventLogTab.TEAM_AND_ACCESS ? asOne(entity) : undefined,
		actorUserId: asText(readFilter(filters, DealEventLogColumn.ACTOR)),
		from,
		to,
	};
};
